<template>
  <div class="settlement-page">
    <div class="page-header">
      <h2>🏦 تسویه سالن‌ها</h2>
      <p class="subtitle">مدیریت تسویه مبالغ بیعانه سالن‌ها</p>
    </div>

    <!-- ========================================== -->
    <!-- دکمه‌های عملیاتی -->
    <!-- ========================================== -->
    <div class="action-bar">
      <button class="btn-primary" @click="generateSettlements" :disabled="loading">
        🔄 تولید تسویه امروز
      </button>
      <button class="btn-success" @click="exportBankExcel" :disabled="!hasPendingSettlements">
        📥 خروجی اکسل (بانک)
      </button>
      <button class="btn-success" @click="exportSettlementReport" :disabled="!selectedSettlement">
        📥 خروجی اکسل (گزارش تسویه)
      </button>
      <button class="btn-secondary" @click="downloadSampleExcel">
        📄 دانلود نمونه فایل اکسل
      </button>
    </div>

    <!-- ========================================== -->
    <!-- آپلود فایل tracking_code -->
    <!-- ========================================== -->
    <div class="upload-section">
      <h4>📤 آپلود فایل اکسل با کد پیگیری</h4>
      <div class="upload-area">
        <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            @change="handleFileUpload"
            style="display: none;"
        />
        <button class="btn-upload" @click="$refs.fileInput.click()" :disabled="uploading">
          {{ uploading ? '⏳ در حال آپلود...' : '📤 انتخاب فایل' }}
        </button>
        <span v-if="uploadResult" class="upload-result">
          {{ uploadResult }}
        </span>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- تب‌ها -->
    <!-- ========================================== -->
    <div class="tabs">
      <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab-btn"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key; loadTabData()"
      >
        {{ tab.icon }} {{ tab.label }}
        <span v-if="tab.count !== undefined" class="tab-count">{{ tab.count }}</span>
      </button>
    </div>

    <!-- ========================================== -->
    <!-- محتوای تب‌ها -->
    <!-- ========================================== -->
    <div class="tab-content">
      <!-- ===== تب ۱: سالن‌های قابل تسویه ===== -->
      <div v-if="activeTab === 'markets'">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <span>در حال بارگذاری...</span>
        </div>

        <div v-else-if="markets.length === 0" class="empty-state">
          <div class="empty-icon">✅</div>
          <h3>هیچ سالنی برای تسویه وجود ندارد</h3>
          <p>همه تراکنش‌ها تسویه شده‌اند.</p>
        </div>

        <div v-else>
          <!-- جدول دسکتاپ -->
          <div class="desktop-table">
            <table>
              <thead>
              <tr>
                <th>#</th>
                <th>نام سالن</th>
                <th>شماره شبا</th>
                <th>تعداد تراکنش</th>
                <th>جمع بیعانه</th>
                <th>کارمزد</th>
                <th>مبلغ واریزی</th>
                <th>وضعیت حساب</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in markets" :key="item.market_id">
                <td>{{ index + 1 }}</td>
                <td class="market-name">{{ item.market_name.title }}</td>
                <td class="iban-cell">{{ item.iban }}</td>
                <td>{{ item.transaction_count }}</td>
                <td class="amount">{{ formatPrice(item.total_deposit) }}</td>
                <td class="commission">{{ formatPrice(item.commission) }}</td>
                <td class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</td>
                <td>
                    <span :class="['badge', item.has_bank_account ? 'badge-success' : 'badge-danger']">
                      {{ item.has_bank_account ? '✓ تایید شده' : '✗ تایید نشده' }}
                    </span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- کارت‌های موبایل -->
          <div class="mobile-cards">
            <div v-for="(item, index) in markets" :key="item.market_id" class="card-item">
              <div class="card-header">
                <span class="card-index">#{{ index + 1 }}</span>
                <span :class="['badge', item.has_bank_account ? 'badge-success' : 'badge-danger']">
                  {{ item.has_bank_account ? '✓ تایید شده' : '✗ تایید نشده' }}
                </span>
              </div>
              <div class="card-body">
                <div class="card-row"><span class="card-label">سالن:</span><span>{{ item.market_name.title }}</span></div>
                <div class="card-row"><span class="card-label">شبا:</span><span class="iban-cell">{{ item.iban }}</span></div>
                <div class="card-row"><span class="card-label">تعداد:</span><span>{{ item.transaction_count }}</span></div>
                <div class="card-row"><span class="card-label">جمع بیعانه:</span><span class="amount">{{ formatPrice(item.total_deposit) }}</span></div>
                <div class="card-row"><span class="card-label">کارمزد:</span><span class="commission">{{ formatPrice(item.commission) }}</span></div>
                <div class="card-row"><span class="card-label">مبلغ واریزی:</span><span class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== تب ۲: در انتظار تسویه ===== -->
      <div v-if="activeTab === 'pending'">
        <div v-if="loading" class="loading-state"><div class="spinner"></div><span>در حال بارگذاری...</span></div>
        <div v-else-if="pendingSettlements.length === 0" class="empty-state">
          <div class="empty-icon">✅</div><h3>هیچ تسویه‌ای در انتظار نیست</h3>
        </div>
        <div v-else>
          <!-- جدول دسکتاپ -->
          <div class="desktop-table">
            <table>
              <thead>
              <tr><th>#</th><th>سالن</th><th>شبا</th><th>تعداد</th><th>جمع بیعانه</th><th>کارمزد</th><th>مبلغ واریزی</th><th>تاریخ</th><th>عملیات</th></tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in pendingSettlements" :key="item.id">
                <td>{{ index + 1 }}</td>
                <td>{{ item.market_name.title }}</td>
                <td class="iban-cell">{{ item.iban}}</td>
                <td>{{ item.transaction_count }}</td>
                <td class="amount">{{ formatPrice(item.total_deposit) }}</td>
                <td class="commission">{{ formatPrice(item.commission_total) }}</td>
                <td class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</td>
                <td>{{ get_date(item.created_at) }}</td>
                <td>
                  <button class="btn-confirm" @click="confirmSettlement(item)">✅ تایید</button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- کارت‌های موبایل -->
          <div class="mobile-cards">
            <div v-for="(item, index) in pendingSettlements" :key="item.id" class="card-item">
              <div class="card-header">
                <span class="card-index">#{{ index + 1 }}</span>
                <span class="badge badge-warning">در انتظار</span>
              </div>
              <div class="card-body">
                <div class="card-row"><span class="card-label">سالن:</span><span>{{ item.market_name.title }}</span></div>
                <div class="card-row"><span class="card-label">تعداد:</span><span>{{ item.transaction_count }}</span></div>
                <div class="card-row"><span class="card-label">جمع:</span><span class="amount">{{ formatPrice(item.total_deposit) }}</span></div>
                <div class="card-row"><span class="card-label">کارمزد:</span><span class="commission">{{ formatPrice(item.commission_total) }}</span></div>
                <div class="card-row"><span class="card-label">مبلغ واریزی:</span><span class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</span></div>
              </div>
              <div class="card-actions">
                <button class="btn-confirm" @click="confirmSettlement(item)">✅ تایید</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== تب ۳: تاریخچه ===== -->
      <div v-if="activeTab === 'history'">
        <div v-if="loading" class="loading-state"><div class="spinner"></div><span>در حال بارگذاری...</span></div>
        <div v-else-if="historyData.length === 0" class="empty-state">
          <div class="empty-icon">📋</div><h3>هیچ تسویه‌ای ثبت نشده</h3>
        </div>
        <div v-else>
          <div class="desktop-table">
            <table>
              <thead>
              <tr><th>#</th><th>سالن</th><th>تعداد</th><th>جمع بیعانه</th><th>کارمزد</th><th>مبلغ واریزی</th><th>کد پیگیری</th><th>وضعیت</th><th>تاریخ</th></tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in historyData" :key="item.id" @click="selectSettlement(item)" :class="{ selected: selectedSettlement && selectedSettlement.id === item.id }">
                <td>{{ index + 1 }}</td>
                <td>{{ item.market_name.title }}</td>
                <td>{{ item.transaction_count }}</td>
                <td class="amount">{{ formatPrice(item.total_deposit) }}</td>
                <td class="commission">{{ formatPrice(item.commission_total) }}</td>
                <td class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</td>
                <td>{{ item.tracking_code }}</td>
                <td><span :class="['badge', statusClass(item.status)]">{{ statusLabel(item.status) }}</span></td>
                <td>{{ get_date(item.created_at) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="mobile-cards">
            <div v-for="(item, index) in historyData" :key="item.id" class="card-item" @click="selectSettlement(item)">
              <div class="card-header">
                <span class="card-index">#{{ index + 1 }}</span>
                <span :class="['badge', statusClass(item.status)]">{{ statusLabel(item.status) }}</span>
              </div>
              <div class="card-body">
                <div class="card-row"><span class="card-label">سالن:</span><span>{{ item.market_name.title }}</span></div>
                <div class="card-row"><span class="card-label">تعداد:</span><span>{{ item.transaction_count }}</span></div>
                <div class="card-row"><span class="card-label">مبلغ واریزی:</span><span class="settlement-amount">{{ formatPrice(item.settlement_amount) }}</span></div>
                <div class="card-row"><span class="card-label">کد پیگیری:</span><span>{{ item.tracking_code }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- مودال تایید تسویه -->
    <!-- ========================================== -->
    <div v-if="showConfirmModal" class="modal-overlay" @click.self="closeConfirmModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>✅ تایید تسویه</h3>
          <button class="btn-close" @click="closeConfirmModal">✕</button>
        </div>
        <div class="modal-body">
          <p><strong>سالن:</strong> {{ confirmItem.market_name.title }}</p>
          <p><strong>مبلغ واریزی:</strong> {{ formatPrice(confirmItem.settlement_amount) }}</p>
          <div class="form-group">
            <label class="required">شماره پیگیری واریز</label>
            <input v-model="trackingCode" type="text" class="form-control" placeholder="شماره پیگیری را وارد کنید..." />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeConfirmModal">انصراف</button>
          <button class="btn-primary" @click="submitConfirm" :disabled="!trackingCode.trim() || confirming">
            {{ confirming ? '⏳ در حال ثبت...' : 'تأیید واریز' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';

export default {
  name: 'SettlementPage',

  data() {
    return {
      loading: false,
      uploading: false,
      confirming: false,
      activeTab: 'markets',
      tabs: [
        { key: 'markets', label: 'سالن‌های قابل تسویه', icon: '🏪' },
        { key: 'pending', label: 'در انتظار تسویه', icon: '⏳' },
        { key: 'history', label: 'تاریخچه', icon: '📋' }
      ],
      markets: [],
      pendingSettlements: [],
      historyData: [],
      selectedSettlement: null,
      showConfirmModal: false,
      confirmItem: null,
      trackingCode: '',
      uploadResult: null
    };
  },

  computed: {
    hasPendingSettlements() {
      return this.pendingSettlements.length > 0;
    }
  },

  mounted() {
    this.loadTabData();
  },

  methods: {
    // ==========================================
    // بارگذاری داده‌ها بر اساس تب
    // ==========================================
    loadTabData() {
      switch (this.activeTab) {
        case 'markets':
          this.loadMarkets();
          break;
        case 'pending':
          this.loadPendingSettlements();
          break;
        case 'history':
          this.loadHistory();
          break;
      }
    },

    // ==========================================
    // بارگذاری سالن‌های قابل تسویه
    // ==========================================
    async loadMarkets() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/super-admin/settlement/markets');
        this.markets = data.data || [];
      } catch (error) {
        console.error('خطا:', error);
        this.$message?.error('خطا در بارگذاری اطلاعات');
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // بارگذاری تسویه‌های در انتظار
    // ==========================================
    async loadPendingSettlements() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/super-admin/settlement/pending');
        this.pendingSettlements = data.data || [];
      } catch (error) {
        console.error('خطا:', error);
        this.$message?.error('خطا در بارگذاری اطلاعات');
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // بارگذاری تاریخچه
    // ==========================================
    async loadHistory() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/super-admin/settlement/history');
        this.historyData = data.data || [];
      } catch (error) {
        console.error('خطا:', error);
        this.$message?.error('خطا در بارگذاری اطلاعات');
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // تولید تسویه
    // ==========================================
    async generateSettlements() {
      if (!confirm('آیا از تولید تسویه برای همه سالن‌ها اطمینان دارید؟')) return;

      this.loading = true;
      try {
        const { data } = await axios.post('/api/super-admin/settlement/generate');
        this.$message?.success(data.message || 'تسویه‌ها با موفقیت تولید شدند.');
        this.loadTabData();
      } catch (error) {
        this.$message?.error(error.response?.data?.message || 'خطا در تولید تسویه');
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // خروجی اکسل برای بانک
    // ==========================================
    exportBankExcel() {
      try {
        // داده‌های مورد نیاز
        const data = this.markets
            .filter(item => item.iban && item.has_bank_account && item.settlement_amount > 0)
            .map(item => ({
              'نام سالن': item.market_name.title,
              'شماره شبا': item.iban,
              'مبلغ واریزی (تومان)': item.settlement_amount,
              'تعداد تراکنش‌ها': item.transaction_count
            }));

        if (data.length === 0) {
          this.$message?.warning('هیچ سالنی با اطلاعات بانکی کامل برای خروجی وجود ندارد.');
          return;
        }

        this.exportToExcel(data, 'تسویه_بانک_' + this.getToday());
      } catch (error) {
        console.error('خطا:', error);
        this.$message?.error('خطا در تولید فایل اکسل');
      }
    },

    // ==========================================
    // خروجی اکسل گزارش یک تسویه
    // ==========================================
    exportSettlementReport() {
      if (!this.selectedSettlement) {
        this.$message?.warning('لطفاً یک تسویه را از تاریخچه انتخاب کنید.');
        return;
      }

      const item = this.selectedSettlement;
      const data = [
        {
          'شناسه تسویه': item.id,
          'سالن': item.market_name.title,
          'تعداد تراکنش‌ها': item.transaction_count,
          'جمع کل بیعانه': item.total_deposit,
          'کارمزد سیستم': item.commission_total,
          'مبلغ نهایی واریزی': item.settlement_amount,
          'کد پیگیری': item.tracking_code || '—',
          'وضعیت': this.statusLabel(item.status),
          'تاریخ تسویه': this.get_date(item.created_at)
        }
      ];

      this.exportToExcel(data, 'گزارش_تسویه_' + item.id);
    },

    // ==========================================
    // تابع اصلی خروجی اکسل
    // ==========================================
    exportToExcel(data, fileName) {
      try {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);

        // تنظیم عرض ستون‌ها
        const colWidths = [];
        Object.keys(data[0] || {}).forEach(key => {
          colWidths.push({ wch: Math.max(key.length * 2, 15) });
        });
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, fileName + '.xlsx');

        this.$message?.success('فایل اکسل با موفقیت دانلود شد.');
      } catch (error) {
        console.error('خطا:', error);
        this.$message?.error('خطا در تولید فایل اکسل');
      }
    },

    // ==========================================
    // دانلود نمونه فایل اکسل
    // ==========================================
    downloadSampleExcel() {
      const sample = [
        { 'شناسه سالن (market_id)': '1', 'کد پیگیری (tracking_code)': 'TRK-12345678' },
        { 'شناسه سالن (market_id)': '2', 'کد پیگیری (tracking_code)': 'TRK-87654321' }
      ];
      this.exportToExcel(sample, 'نمونه_فایل_کد_پیگیری');
    },

    // ==========================================
    // آپلود فایل اکسل با کد پیگیری
    // ==========================================
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploading = true;
      this.uploadResult = null;

      try {
        // ==========================================
        // 1. خواندن فایل با xlsx در Vue
        // ==========================================
        const data = await this.readExcelFile(file);

        // ==========================================
        // 2. ارسال JSON به کنترلر
        // ==========================================
        const response = await axios.post('/api/super-admin/settlement/save-tracking', {
          data: data
        });

        this.uploadResult = '✅ فایل با موفقیت پردازش شد.';
        this.$message?.success(response.data.message || 'فایل با موفقیت پردازش شد.');
        this.loadTabData();

      } catch (error) {
        this.uploadResult = '❌ خطا در پردازش فایل';
        this.$message?.error(error.response?.data?.message || 'خطا در پردازش فایل');
        console.error(error);
      } finally {
        this.uploading = false;
        event.target.value = '';
      }
    },
    readExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const workbook = XLSX.read(e.target.result, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet);

            // تبدیل به فرمت مورد نیاز
            const result = jsonData.map(row => ({
              market_id: row['شناسه سالن (market_id)'] || row['market_id'],
              tracking_code: row['کد پیگیری (tracking_code)'] || row['tracking_code']
            }));

            resolve(result);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
      });
    },
    // ==========================================
    // تایید تسویه
    // ==========================================
    confirmSettlement(item) {
      this.confirmItem = item;
      this.trackingCode = '';
      this.showConfirmModal = true;
    },

    closeConfirmModal() {
      this.showConfirmModal = false;
      this.confirmItem = null;
      this.trackingCode = '';
    },

    async submitConfirm() {
      if (!this.trackingCode.trim()) {
        this.$message?.warning('لطفاً شماره پیگیری را وارد کنید.');
        return;
      }

      this.confirming = true;
      try {
        await axios.post('/api/super-admin/settlement/' + this.confirmItem.id + '/confirm', {
          tracking_code: this.trackingCode.trim()
        });

        this.$message?.success('تسویه با موفقیت تایید شد.');
        this.closeConfirmModal();
        this.loadTabData();
      } catch (error) {
        this.$message?.error(error.response?.data?.message || 'خطا در تایید تسویه');
      } finally {
        this.confirming = false;
      }
    },

    // ==========================================
    // انتخاب تسویه از تاریخچه
    // ==========================================
    selectSettlement(item) {
      this.selectedSettlement = this.selectedSettlement?.id === item.id ? null : item;
    },

    // ==========================================
    // توابع کمکی
    // ==========================================
    formatPrice(value) {
      if (!value) return '۰';
      return new Intl.NumberFormat('fa-IR').format(value);
    },

    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.getFullYear() + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0');
    },

    getToday() {
      const d = new Date();
      return d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
    },

    statusLabel(status) {
      const map = { pending: 'در انتظار', paid: 'واریز شده', failed: 'ناموفق' };
      return map[status] || status;
    },

    statusClass(status) {
      const map = { pending: 'badge-warning', paid: 'badge-success', failed: 'badge-danger' };
      return map[status] || 'badge-gray';
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* استایل‌های اصلی */
/* ========================================== */
.settlement-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  direction: rtl;
}

.page-header {
  margin-bottom: 24px;
}
.page-header h2 { margin: 0; color: #2c3e50; font-size: 24px; }
.page-header .subtitle { margin: 4px 0 0; color: #909399; font-size: 14px; }

/* ========================================== */
/* نوار عملیات */
/* ========================================== */
.action-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  background: white;
  padding: 16px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.btn-primary, .btn-success, .btn-secondary, .btn-upload {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-primary { background: #409eff; color: white; }
.btn-primary:hover:not(:disabled) { background: #66b1ff; }

.btn-success { background: #67c23a; color: white; }
.btn-success:hover:not(:disabled) { background: #85ce61; }

.btn-secondary { background: #f5f7fa; color: #606266; border: 1px solid #dcdfe6; }
.btn-secondary:hover { background: #e4e7ed; }

.btn-upload { background: #e6a23c; color: white; }
.btn-upload:hover:not(:disabled) { background: #ebb563; }

.btn-primary:disabled, .btn-success:disabled { opacity: 0.5; cursor: not-allowed; }

/* ========================================== */
/* بخش آپلود */
/* ========================================== */
.upload-section {
  background: #f8f9fa;
  padding: 16px 20px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.upload-section h4 { margin: 0 0 10px 0; color: #2c3e50; }
.upload-area { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.upload-result { color: #67c23a; font-weight: 500; }

/* ========================================== */
/* تب‌ها */
/* ========================================== */
.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  background: #f5f7fa;
  padding: 6px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.tab-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #606266;
  transition: all 0.3s;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tab-btn:hover { background: #e8f0fe; }
.tab-btn.active { background: white; color: #409eff; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.tab-count { background: #409eff; color: white; border-radius: 50%; padding: 0 8px; font-size: 11px; }

/* ========================================== */
/* جدول و کارت‌ها */
/* ========================================== */
.desktop-table {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  overflow-x: auto;
}

.desktop-table table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 800px; }
.desktop-table thead th { background: #f5f7fa; color: #606266; font-weight: 600; padding: 12px 14px; text-align: right; border-bottom: 2px solid #e4e7ed; }
.desktop-table tbody td { padding: 10px 14px; border-bottom: 1px solid #ebeef5; color: #303133; }
.desktop-table tbody tr:hover td { background: #f5f7fa; }
.desktop-table tbody tr.selected td { background: #ecf5ff; }

.market-name { font-weight: 500; }
.iban-cell { font-family: monospace; font-size: 12px; direction: ltr; }
.amount { font-weight: 600; }
.commission { color: #f56c6c; }
.settlement-amount { font-weight: bold; color: #67c23a; }

/* ========================================== */
/* بج‌ها */
/* ========================================== */
.badge { display: inline-block; padding: 2px 12px; border-radius: 12px; font-size: 11px; font-weight: 500; }
.badge-success { background: #f0f9eb; color: #67c23a; }
.badge-danger { background: #fef0f0; color: #f56c6c; }
.badge-warning { background: #fdf6ec; color: #e6a23c; }
.badge-gray { background: #f4f4f5; color: #909399; }

/* ========================================== */
/* دکمه‌های عملیات */
/* ========================================== */
.btn-confirm { padding: 4px 14px; background: #f0f9eb; color: #67c23a; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; transition: all 0.2s; }
.btn-confirm:hover { background: #67c23a; color: white; }

/* ========================================== */
/* حالت بارگذاری و خالی */
/* ========================================== */
.loading-state { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; color: #909399; gap: 12px; background: white; border-radius: 10px; }
.spinner { width: 36px; height: 36px; border: 4px solid #f3f3f3; border-top: 4px solid #409eff; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

.empty-state { text-align: center; padding: 60px 20px; background: white; border-radius: 10px; }
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-state h3 { color: #2c3e50; margin-bottom: 4px; }
.empty-state p { color: #909399; }

/* ========================================== */
/* کارت‌های موبایل */
/* ========================================== */
.mobile-cards { display: none; }
.card-item { background: white; border-radius: 8px; padding: 14px 16px; margin-bottom: 10px; box-shadow: 0 2px 6px rgba(0,0,0,0.06); cursor: pointer; }
.card-item:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #f0f0f0; }
.card-index { font-weight: 600; color: #909399; font-size: 13px; }
.card-body { display: flex; flex-direction: column; gap: 4px; }
.card-row { display: flex; gap: 6px; font-size: 13px; color: #303133; flex-wrap: wrap; }
.card-label { font-weight: 500; color: #909399; min-width: 80px; }
.card-actions { display: flex; gap: 8px; margin-top: 10px; padding-top: 10px; border-top: 1px solid #f0f0f0; }
.card-actions .btn-confirm { flex: 1; padding: 8px; text-align: center; }

/* ========================================== */
/* مودال */
/* ========================================== */
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; animation: fadeIn 0.3s; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.modal-content { background: white; border-radius: 12px; max-width: 450px; width: 92%; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s; }
@keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #f0f0f0; }
.modal-header h3 { margin: 0; color: #2c3e50; }
.btn-close { background: none; border: none; font-size: 22px; cursor: pointer; color: #909399; }
.btn-close:hover { color: #2c3e50; }

.modal-body { padding: 20px; }
.modal-body p { margin: 6px 0; color: #606266; }
.modal-body p strong { color: #2c3e50; }

.form-group label { display: block; font-weight: 600; font-size: 14px; color: #2c3e50; margin-bottom: 6px; }
.form-group label.required::after { content: '*'; color: #f56c6c; margin-right: 4px; }
.form-control { width: 100%; padding: 10px 12px; border: 1px solid #dcdfe6; border-radius: 6px; font-size: 14px; box-sizing: border-box; }
.form-control:focus { outline: none; border-color: #409eff; }

.modal-footer { padding: 16px 20px; border-top: 1px solid #f0f0f0; display: flex; gap: 10px; justify-content: flex-end; }

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .settlement-page { padding: 12px; }
  .action-bar { flex-direction: column; }
  .action-bar .btn-primary, .action-bar .btn-success, .action-bar .btn-secondary, .action-bar .btn-upload { width: 100%; text-align: center; }
  .upload-area { flex-direction: column; align-items: stretch; }
  .desktop-table { display: none; }
  .mobile-cards { display: block; }
  .tabs { flex-direction: column; }
  .tab-btn { width: 100%; text-align: center; justify-content: center; }
  .modal-content { width: 95%; }
}

@media (max-width: 480px) {
  .settlement-page { padding: 8px; }
  .page-header h2 { font-size: 20px; }
  .tab-btn { font-size: 13px; padding: 8px 12px; }
}
</style>